import { useEffect, useRef, useState, useCallback } from 'react';

const NEAR_BOTTOM_PX = 80;

/**
 * Smart chat scroll: follow new messages when pinned to bottom; respect scroll-up reading.
 * @param {Array<{ id?: string }>} messages
 * @param {unknown[]} resetDeps — changing room/thread resets to latest (e.g. [roomId])
 */
export function useChatAutoScroll(messages, resetDeps = []) {
  const containerRef = useRef(null);
  const endRef = useRef(null);
  const forceScrollRef = useRef(false);
  const initialScrollDoneRef = useRef(false);
  const prevSignatureRef = useRef('');
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const isNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    endRef.current?.scrollIntoView({ behavior, block: 'end' });
    forceScrollRef.current = false;
    setShowJumpToLatest(false);
  }, []);

  const markForceScroll = useCallback(() => {
    forceScrollRef.current = true;
  }, []);

  const handleScroll = useCallback(() => {
    if (forceScrollRef.current) return;
    setShowJumpToLatest(!isNearBottom() && messages.length > 0);
  }, [isNearBottom, messages.length]);

  useEffect(() => {
    initialScrollDoneRef.current = false;
    prevSignatureRef.current = '';
    forceScrollRef.current = true;
    setShowJumpToLatest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when thread/room changes
  }, resetDeps);

  useEffect(() => {
    const last = messages[messages.length - 1];
    const signature = `${messages.length}:${last?.id ?? ''}`;
    if (signature === prevSignatureRef.current) return;
    prevSignatureRef.current = signature;

    const shouldScroll =
      forceScrollRef.current || !initialScrollDoneRef.current || isNearBottom();

    if (!shouldScroll) {
      if (messages.length > 0) setShowJumpToLatest(true);
      return;
    }

    if (messages.length === 0) {
      setShowJumpToLatest(false);
      return;
    }

    const behavior = initialScrollDoneRef.current ? 'smooth' : 'auto';
    const id = requestAnimationFrame(() => {
      scrollToBottom(behavior);
      initialScrollDoneRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [messages, scrollToBottom, isNearBottom]);

  return {
    containerRef,
    endRef,
    scrollToBottom,
    markForceScroll,
    handleScroll,
    showJumpToLatest,
  };
}
